/// <reference types="vite-plugin-svgr/client" />
import { AppBar, Stack, Toolbar, Link as MuiLink, Typography } from '@mui/material';
import { Link, Navigate, Outlet } from 'react-router-dom';

import { useManageChat } from '~/modules/@shared/hooks/useManageChat';
import { LocationProvider } from '~/contexts/location.context';
import { useGetUserQuery } from '~/services/user';
import { useEffect, useMemo, useState } from 'react';
import { User } from '~/services/types';
import { array } from '~/modules/@shared/helpers/array';

import Logo from '~/assets/images/logo.svg?react';
import { useKioskStore } from '~/modules/kiosk/hooks/useKioskStore';
import { hexToLuma } from '~/modules/@shared/helpers/hexToLuma';
import { PendoPageTracker } from '~/modules/@shared/components';

export default function KioskLayout() {
  const { data: user, isPending: isUserPending } = useGetUserQuery();
  const [closeChat, setCloseChat] = useState(false);
  const { tryChat } = useManageChat({ disabled: true, close: closeChat });
  const { tryChat: tryCLoseChat } = useManageChat({ disabled: true, close: true });

  const location = useKioskStore((state) => state.location);

  useEffect(() => {
    const id = setTimeout(() => {
      setCloseChat(false);
      tryChat();
    }, 50);
    return () => {
      clearTimeout(id);
      tryCLoseChat();
    };
  }, [tryCLoseChat, tryChat]);

  const mainLocation = useMemo(() => {
    if (!user) return null;

    const defaultLocation = array((user as User).locations)
      .filter(Boolean)
      .find((y) => y.primary);
    if (!defaultLocation) return null;

    return defaultLocation;
  }, [user]);

  if (isUserPending) return null;

  if (!user || !mainLocation) {
    return <Navigate to="/login" />;
  }

  return (
    <LocationProvider defaultLocation={mainLocation}>
      <Stack
        sx={{
          backgroundColor: '#f6f7fb',
          minHeight: '100dvh',
        }}>
        <AppBar
          component="nav"
          sx={{
            zIndex: (theme) => theme.zIndex.drawer + 1,
            backgroundColor: location?.accent_color ?? 'background.header',
          }}>
          <Toolbar>
            <MuiLink component={Link} to="/login" sx={{ width: 116, display: 'inline-block' }}>
              {location?.logo_url ? <img src={location.logo_url} /> : <Logo />}
            </MuiLink>
            <Stack
              direction="row"
              spacing={1}
              sx={{
                display: { xs: 'none', md: 'inherit' },
                textAlign: 'center',
                justifyContent: 'center',
                margin: 'auto',
              }}>
              <Typography
                sx={{
                  fontSize: '1.5rem',
                  fontWeight: 'bold',
                  color: hexToLuma(location?.accent_color ?? '#000000') > 0.5 ? 'black' : 'white',
                }}>
                CHECK-IN
              </Typography>
            </Stack>
          </Toolbar>
        </AppBar>

        <Stack sx={{ flex: 1 }} spacing={2}>
          <Toolbar />
          <Outlet />
          <div id="sScancode" style={{ textAlign: 'center', fontSize: '3rem' }}></div>
        </Stack>
        
        {/* Pendo page tracking for authenticated users */}
        <PendoPageTracker />
      </Stack>
    </LocationProvider>
  );
}
